import { RadioButton, Theme, Tag, TagStyle, useTranslation, FAIcon } from '@nextgen-web-framework/all';
import { ValidationType } from '@commerce-profile-webcomponents/sdk';
import type { Contact } from '@commerce-profile-webcomponents/sdk';
import { useAddressFormat, getAddressName } from '../../../../utils/myAccountAddressUtils';
import { extractPhoneNumber } from '../../../../utils/helper';
import PhoneNumberDisplay from '../../../Common/PhoneNumberDisplay/PhoneNumberDisplay';
import BadAddressAlert from '../../../AddressCommon/BadAddressAlert/BadAddressAlert';
import type { AddressCardProps } from './types/propTypes';
import styles from './AddressCard.module.scss';

const AddressCard = ({
  userAddress,
  selectedAddress,
  onAddressSelect,
  onAddressEdit,
  isDownlineAddressOpen,
  allowEdit = true,
  allowSelectInvalidAddress = false,
  allowEditOnDefault,
  defaultTagTheme = Theme.PURPLE, // Default theme for the tag
}: AddressCardProps) => {
  const { isDefault, deliveryInstructions, phone, recipientFirstName, recipientLastName, validationStatus } =
    userAddress as Contact;
  const t = useTranslation();
  const addrFormat = useAddressFormat;

  const formattedAddress = addrFormat(userAddress);

  const { dialCode, number: phoneNumber } = extractPhoneNumber(phone ?? '');
  const delvInstruction = deliveryInstructions?.trim();
  const classNameForDefault = isDefault ? styles['default-address'] : '';

  const isBadAddressFlag = validationStatus === ValidationType.NotValidated;
  const badAddressClassName = isBadAddressFlag ? styles['bad-address'] : '';

  const allowEditFlag =
    (!isDefault && allowEdit) || (allowEdit && allowEditOnDefault && isDefault && !isDownlineAddressOpen);

  const cardCTA = () => {
    if (isBadAddressFlag && allowEdit) {
      // This control is for bad address edit
      onAddressEdit?.(userAddress as Contact);
    } else if ((isBadAddressFlag && allowSelectInvalidAddress) || !isBadAddressFlag) {
      onAddressSelect?.(userAddress as Contact);
    }
  };

  return (
    <div
      className={`${styles['address-card-container']} ${badAddressClassName}`}
      data-testid="address-card-testid"
      onClick={cardCTA}
      role="button"
      aria-label="address-card"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          cardCTA();
        }
      }}
    >
      <div className={styles['address-card-header']}>
        <div className={`${styles['address-card-head-tag']}`}>
          <div className={`${styles['address-nick-name']} ${classNameForDefault}`}>
            {getAddressName(userAddress as unknown as Contact, t)}
          </div>
          <div className={styles['address-details']}>
            <div>
              <div>
                {t('profile.addressCard.recipientName', {
                  // let translation to handle what to show
                  recipientFirstName: recipientFirstName ?? '',
                  recipientLastName: recipientLastName ?? '',
                })}
              </div>
              <div>
                <PhoneNumberDisplay countryCode={dialCode} phoneNumber={phoneNumber} />
              </div>
            </div>

            <div dangerouslySetInnerHTML={{ __html: formattedAddress }}></div>

            {deliveryInstructions && (
              <div>
                <span>{delvInstruction && `‘${delvInstruction}’`}</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles['address-radio']}>
          <RadioButton
            id="radio"
            data-testid="test-radio"
            className="filter-radio"
            checked={selectedAddress}
            theme={selectedAddress ? Theme.PURPLE : Theme.BLACK}
            disabled={isBadAddressFlag && !allowSelectInvalidAddress}
            onClick={() => {
              onAddressSelect && onAddressSelect(userAddress as Contact);
            }}
          />

          {/* If it's bad address, not required to show the edit icon, onclick to open edit form */}
          {!isBadAddressFlag && allowEditFlag && (
            <div className={styles['icon']}>
              {!isDownlineAddressOpen && (
                <FAIcon
                  theme={selectedAddress ? Theme.PURPLE : Theme.BLACK}
                  data-testid="test-icon-id"
                  iconName={'pen-to-square'}
                  onClick={() => onAddressEdit?.(userAddress as Contact)}
                  className="verified_icon LIGHT"
                />
              )}
            </div>
          )}
        </div>

        {isDefault && (
          <div className={styles['default-tag']} data-testid="default-tag-testid">
            <Tag text={t('profile.addressCard.default')} style={TagStyle.SOLID} theme={defaultTagTheme} />
          </div>
        )}
      </div>

      {isBadAddressFlag && (
        <div className={`${styles['bad-address-container']} `} data-testid="bad-address-container-testid">
          <BadAddressAlert />
        </div>
      )}
    </div>
  );
};

export default AddressCard;
